import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Filter, Globe, Package, Sparkles, AlertTriangle, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import SAR from '../components/SAR';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIOD_OPTIONS = [
  { value: 'weekly', label: 'أسبوعي' },
  { value: 'monthly', label: 'شهري' },
  { value: 'quarterly', label: 'ربع سنوي' },
  { value: 'custom', label: 'تاريخ مخصص' },
];

const today = () => new Date().toISOString().split('T')[0];

const TREND_META = {
  up: { color: 'text-success', bg: 'bg-success/10', icon: TrendingUp, label: 'نمو' },
  down: { color: 'text-destructive', bg: 'bg-destructive/10', icon: TrendingDown, label: 'انخفاض' },
  flat: { color: 'text-foreground/60', bg: 'bg-foreground/5', icon: Minus, label: 'ثابت' },
  new: { color: 'text-brand_blue', bg: 'bg-brand_blue/10', icon: Sparkles, label: 'جديد' },
  lost: { color: 'text-amber-700', bg: 'bg-amber-100', icon: AlertTriangle, label: 'متوقف' },
};

function TrendBadge({ trend, percentage }) {
  const meta = TREND_META[trend] || TREND_META.flat;
  const Icon = meta.icon;
  const showPct = trend !== 'new' && trend !== 'lost';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-bold text-xs ${meta.bg} ${meta.color}`}
      data-testid={`trend-badge-${trend}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {showPct ? (
        <>
          {percentage > 0 ? '+' : ''}
          <span className="font-numbers">{percentage}%</span>
        </>
      ) : (
        meta.label
      )}
    </span>
  );
}

export default function Growth() {
  const [dimension, setDimension] = useState('country');
  const [period, setPeriod] = useState('monthly');
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ dimension, period });
      if (period === 'custom') {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      const res = await axios.get(`${API_URL}/api/analytics/growth?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [dimension, period, startDate, endDate]);

  useEffect(() => {
    if (period !== 'custom') fetchData();
  }, [dimension, period, fetchData]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ dimension, period });
      if (period === 'custom') {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      const res = await axios.get(
        `${API_URL}/api/analytics/growth/export/${format}?${params.toString()}`,
        { responseType: 'blob' }
      );
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `growth_${dimension}_${period}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`تم تصدير ${format === 'excel' ? 'Excel' : 'PDF'} بنجاح`);
    } catch (err) {
      toast.error('فشل التصدير');
    } finally {
      setExporting(false);
    }
  };

  const items = data?.items || [];
  const chartData = items.slice(0, 10).map((it) => ({
    name: it.name,
    'الفترة الحالية': it.current_total,
    'الفترة السابقة': it.previous_total,
  }));

  const overallTrend = TREND_META[data?.trend || 'flat'];
  const OverallIcon = overallTrend.icon;

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('ar-SA', {
        numberingSystem: 'latn',
        calendar: 'gregory',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch (_e) {
      return iso.slice(0, 10);
    }
  };

  return (
    <div className="space-y-6" dir="rtl" data-testid="growth-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-brand_blue/10 flex items-center justify-center">
            <TrendingUp className="h-6 w-6 text-brand_blue" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-foreground">نمو وانخفاض المبيعات</h1>
            <p className="text-sm text-foreground/60 mt-1">مقارنة الفترة الحالية بالفترة السابقة بنفس المدة — للدول والمنتجات</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="export-excel-btn"
            onClick={() => handleExport('excel')}
            variant="outline"
            className="gap-2 border-success/40 text-success hover:bg-success/10"
            disabled={loading || exporting}
          >
            <FileSpreadsheet className="h-4 w-4" />
            {exporting ? 'جاري التصدير...' : 'تصدير Excel'}
          </Button>
          <Button
            data-testid="export-pdf-btn"
            onClick={() => handleExport('pdf')}
            variant="outline"
            className="gap-2 border-destructive/40 text-destructive hover:bg-destructive/10"
            disabled={loading || exporting}
          >
            <FileText className="h-4 w-4" />
            {exporting ? 'جاري التصدير...' : 'تصدير PDF'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-brand_blue" />
          <h3 className="font-bold">الفلاتر</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="space-y-2">
            <Label>التحليل حسب</Label>
            <div className="flex gap-2">
              <Button
                data-testid="dimension-country-btn"
                size="sm"
                variant={dimension === 'country' ? 'default' : 'outline'}
                onClick={() => setDimension('country')}
                className="flex-1 gap-1.5"
              >
                <Globe className="h-4 w-4" />
                الدولة
              </Button>
              <Button
                data-testid="dimension-product-btn"
                size="sm"
                variant={dimension === 'product' ? 'default' : 'outline'}
                onClick={() => setDimension('product')}
                className="flex-1 gap-1.5"
              >
                <Package className="h-4 w-4" />
                المنتج
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>الفترة</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="growth-period-select" className="text-right">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {period === 'custom' && (
            <>
              <div className="space-y-2">
                <Label>من تاريخ</Label>
                <Input
                  data-testid="growth-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="text-right font-numbers"
                />
              </div>
              <div className="space-y-2">
                <Label>إلى تاريخ</Label>
                <Input
                  data-testid="growth-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="text-right font-numbers"
                />
              </div>
              <Button
                data-testid="growth-apply-filter"
                onClick={fetchData}
                disabled={loading}
                className="gap-2 md:col-span-1"
              >
                <Filter className="h-4 w-4" />
                {loading ? 'جاري...' : 'تطبيق'}
              </Button>
            </>
          )}
        </div>
      </Card>

      {/* Overall summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5">
          <p className="text-xs text-foreground/60 mb-2">الفترة الحالية</p>
          <p className="text-2xl font-black text-success font-numbers" data-testid="current-total">
            {(data?.current?.total || 0).toLocaleString('en-US')} <SAR />
          </p>
          <p className="text-xs text-foreground/50 mt-2 font-numbers">
            {formatDate(data?.current?.start)} → {formatDate(data?.current?.end)}
          </p>
          <p className="text-xs text-foreground/50 font-numbers">
            {data?.current?.transactions || 0} معاملة
          </p>
        </Card>
        <Card className="p-5">
          <p className="text-xs text-foreground/60 mb-2">الفترة السابقة</p>
          <p className="text-2xl font-black text-foreground/70 font-numbers" data-testid="previous-total">
            {(data?.previous?.total || 0).toLocaleString('en-US')} <SAR />
          </p>
          <p className="text-xs text-foreground/50 mt-2 font-numbers">
            {formatDate(data?.previous?.start)} → {formatDate(data?.previous?.end)}
          </p>
          <p className="text-xs text-foreground/50 font-numbers">
            {data?.previous?.transactions || 0} معاملة
          </p>
        </Card>
        <Card className={`p-5 ${overallTrend.bg}`} data-testid="overall-growth-card">
          <p className="text-xs text-foreground/60 mb-2">نمو الإجمالي</p>
          <div className={`flex items-center gap-2 ${overallTrend.color}`}>
            <OverallIcon className="h-7 w-7" />
            <p className="text-2xl font-black font-numbers" data-testid="overall-growth-percentage">
              {(data?.growth_percentage ?? 0) > 0 ? '+' : ''}{data?.growth_percentage ?? 0}%
            </p>
          </div>
          <p className={`text-xs mt-2 font-numbers ${overallTrend.color}`}>
            {(data?.growth_amount ?? 0) > 0 ? '+' : ''}{(data?.growth_amount ?? 0).toLocaleString('en-US')} <SAR />
          </p>
        </Card>
      </div>

      {/* Comparison Chart */}
      <Card className="p-5">
        <h3 className="font-bold mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-brand_blue" />
          مقارنة بصرية — أعلى 10
        </h3>
        {chartData.length === 0 ? (
          <div className="text-center py-12 text-foreground/40">لا توجد بيانات في الفترة المحددة</div>
        ) : (
          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} />
                <YAxis tick={{ fontSize: 11, fill: '#374151' }} />
                <Tooltip
                  formatter={(v) => `${(v ?? 0).toLocaleString('en-US')} ر.س`}
                  contentStyle={{ direction: 'rtl', textAlign: 'right', fontSize: 12 }}
                />
                <Legend wrapperStyle={{ direction: 'rtl' }} />
                <Bar dataKey="الفترة السابقة" fill="#94A3B8" radius={[6, 6, 0, 0]} />
                <Bar dataKey="الفترة الحالية" fill="#1E40AF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Items table */}
      <Card className="border-border">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="growth-table">
            <thead className="bg-accent/50">
              <tr>
                <th className="text-right py-3 px-4 text-sm font-bold">#</th>
                <th className="text-right py-3 px-4 text-sm font-bold">{dimension === 'country' ? 'الدولة' : 'المنتج'}</th>
                <th className="text-right py-3 px-4 text-sm font-bold">الفترة الحالية</th>
                <th className="text-right py-3 px-4 text-sm font-bold">الفترة السابقة</th>
                <th className="text-right py-3 px-4 text-sm font-bold">الفرق</th>
                <th className="text-right py-3 px-4 text-sm font-bold">الاتجاه</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-8 text-muted-foreground">لا توجد بيانات</td></tr>
              ) : (
                items.map((it, i) => (
                  <tr key={it.name} className="border-t border-border hover:bg-accent/30">
                    <td className="py-3 px-4 font-numbers text-foreground/50">{i + 1}</td>
                    <td className="py-3 px-4 font-bold">{it.name}</td>
                    <td className="py-3 px-4 text-success font-bold font-numbers">
                      {it.current_total.toLocaleString('en-US')} <SAR />
                      <span className="text-xs text-foreground/50 mr-2 font-normal">({it.current_count})</span>
                    </td>
                    <td className="py-3 px-4 text-foreground/70 font-numbers">
                      {it.previous_total.toLocaleString('en-US')} <SAR />
                      <span className="text-xs text-foreground/50 mr-2 font-normal">({it.previous_count})</span>
                    </td>
                    <td className={`py-3 px-4 font-bold font-numbers ${it.growth_amount > 0 ? 'text-success' : it.growth_amount < 0 ? 'text-destructive' : 'text-foreground/60'}`}>
                      {it.growth_amount > 0 ? '+' : ''}{it.growth_amount.toLocaleString('en-US')} <SAR />
                    </td>
                    <td className="py-3 px-4">
                      <TrendBadge trend={it.trend} percentage={it.growth_percentage} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
